import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE = (process.env.REACT_APP_API_BASE || "") + "/api/analysis";

const getDefaultConfigByType = (type) => {
  if (type === "distance_inverse") {
    return {
      max_distance_km: 1,
      min_score: 0,
      max_score: 100,
    };
  }

  if (type === "threshold_band") {
    return {
      bands: [
        { max: 0.5, score: 100 },
        { max: 1.0, score: 80 },
        { max: 2.0, score: 50 },
        { max: 9999, score: 0 },
      ],
    };
  }

  if (type === "boolean") {
    return {
      true_score: 100,
      false_score: 0,
    };
  }

  return {};
};

const normalizeItems = (items) =>
  items.map((item) => ({
    ...item,
    description: item.description || "",
    config:
      item.config && typeof item.config === "object"
        ? item.config
        : getDefaultConfigByType(item.scoring_type),
  }));

const statCardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: "16px 18px",
};

const statLabelStyle = {
  color: "#667085",
  fontSize: 14,
  marginBottom: 6,
};

const statValueStyle = {
  fontSize: 28,
  fontWeight: 700,
  color: "#101828",
};

const labelStyle = {
  display: "block",
  marginBottom: 6,
  fontWeight: 600,
  color: "#344054",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #d0d5dd",
  boxSizing: "border-box",
};

const buttonBaseStyle = {
  padding: "10px 16px",
  borderRadius: 10,
  fontWeight: 600,
  cursor: "pointer",
};

const ScoreCriteriaDashboard = () => {
  const [criteria, setCriteria] = useState([]);
  const [originalCriteria, setOriginalCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const fetchCriteria = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      const res = await axios.get(`${API_BASE}/score-criteria/`);
      const rows = Array.isArray(res.data) ? res.data : res.data.results || [];
      const normalized = normalizeItems(rows);

      setCriteria(normalized);
      setOriginalCriteria(normalized);
    } catch (err) {
      console.error("Failed to load criteria:", err);
      setError("Failed to load score criteria.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCriteria();
  }, []);

  const handleFieldChange = (id, field, value) => {
    setCriteria((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        if (field === "scoring_type") {
          return {
            ...item,
            scoring_type: value,
            config: getDefaultConfigByType(value),
          };
        }

        return { ...item, [field]: value };
      })
    );
  };

  const handleConfigFieldChange = (id, key, value) => {
    setCriteria((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              config: {
                ...item.config,
                [key]: value,
              },
            }
          : item
      )
    );
  };

  const handleBandChange = (id, index, field, value) => {
    setCriteria((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const bands = [...(item.config?.bands || [])];
        bands[index] = {
          ...bands[index],
          [field]: Number(value),
        };

        return {
          ...item,
          config: {
            ...item.config,
            bands,
          },
        };
      })
    );
  };

  const addBand = (id) => {
    setCriteria((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const bands = [...(item.config?.bands || []), { max: 0, score: 0 }];

        return {
          ...item,
          config: {
            ...item.config,
            bands,
          },
        };
      })
    );
  };

  const removeBand = (id, index) => {
    setCriteria((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const bands = [...(item.config?.bands || [])];
        bands.splice(index, 1);

        return {
          ...item,
          config: {
            ...item.config,
            bands,
          },
        };
      })
    );
  };

  const hasChanged = (item) => {
    const original = originalCriteria.find((x) => x.id === item.id);
    if (!original) return false;

    return (
      item.title !== original.title ||
      item.description !== original.description ||
      String(item.weight) !== String(original.weight) ||
      String(item.order) !== String(original.order) ||
      item.scoring_type !== original.scoring_type ||
      item.is_active !== original.is_active ||
      JSON.stringify(item.config) !== JSON.stringify(original.config)
    );
  };

  const handleReset = (id) => {
    const original = originalCriteria.find((x) => x.id === id);
    if (!original) return;

    setCriteria((prev) =>
      prev.map((item) => (item.id === id ? { ...original } : item))
    );
  };

  const handleSave = async (item) => {
    try {
      setSavingId(item.id);
      setError("");
      setSuccessMessage("");

      const payload = {
        title: item.title,
        description: item.description || "",
        is_active: item.is_active,
        weight: Number(item.weight),
        scoring_type: item.scoring_type,
        config: item.config || {},
        order: Number(item.order),
      };

      await axios.patch(`${API_BASE}/score-criteria/${item.id}/`, payload);

      setSuccessMessage(`"${item.title}" was saved successfully.`);
      await fetchCriteria();
    } catch (err) {
      console.error("Save failed:", err);
      setError(`Failed to save "${item.title}".`);
    } finally {
      setSavingId(null);
    }
  };

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      setError("");
      setSuccessMessage("");

      const res = await axios.post(`${API_BASE}/recalculate-scores/`);
      setSuccessMessage(
        `Recalculation completed. Processed: ${res.data.processed}`
      );
    } catch (err) {
      console.error("Recalculate failed:", err);
      setError("Failed to recalculate scores.");
    } finally {
      setRecalculating(false);
    }
  };

  const stats = useMemo(() => {
    const total = criteria.length;
    const active = criteria.filter((c) => c.is_active).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [criteria]);

  if (loading) {
    return <div style={{ padding: 24 }}>Loading criteria...</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ marginBottom: 8 }}>Score Criteria Management</h1>
        <p style={{ margin: 0, color: "#555" }}>
          Manage weights, scoring rules, and activation status for UrbanScore
          ranking.
        </p>
      </div>

      {(error || successMessage) && (
        <div style={{ marginBottom: 20 }}>
          {error && (
            <div
              style={{
                background: "#fdecea",
                color: "#b42318",
                border: "1px solid #f5c2c7",
                padding: "12px 16px",
                borderRadius: 10,
                marginBottom: 10,
              }}
            >
              {error}
            </div>
          )}

          {successMessage && (
            <div
              style={{
                background: "#ecfdf3",
                color: "#027a48",
                border: "1px solid #abefc6",
                padding: "12px 16px",
                borderRadius: 10,
              }}
            >
              {successMessage}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div style={statCardStyle}>
          <div style={statLabelStyle}>Total Criteria</div>
          <div style={statValueStyle}>{stats.total}</div>
        </div>

        <div style={statCardStyle}>
          <div style={statLabelStyle}>Active</div>
          <div style={statValueStyle}>{stats.active}</div>
        </div>

        <div style={statCardStyle}>
          <div style={statLabelStyle}>Inactive</div>
          <div style={statValueStyle}>{stats.inactive}</div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0 }}>Criteria List</h2>

        <button
          onClick={handleRecalculate}
          disabled={recalculating}
          style={{
            ...buttonBaseStyle,
            border: "none",
            background: "#f28c28",
            color: "#fff",
          }}
        >
          {recalculating ? "Recalculating..." : "Recalculate Scores"}
        </button>
      </div>

      <div style={{ display: "grid", gap: 18 }}>
        {criteria.map((item) => {
          const changed = hasChanged(item);

          return (
            <div
              key={item.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 18,
                background: "#fff",
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 14,
                }}
              >
                <div>
                  <h3 style={{ margin: 0 }}>{item.title || item.key}</h3>
                  <div style={{ color: "#666", marginTop: 4 }}>{item.key}</div>
                </div>

                {changed && (
                  <span
                    style={{
                      background: "#fff7e6",
                      color: "#b54708",
                      padding: "6px 10px",
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    Unsaved changes
                  </span>
                )}
              </div>

              <div style={{ display: "grid", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Title</label>
                  <input
                    type="text"
                    value={item.title || ""}
                    onChange={(e) =>
                      handleFieldChange(item.id, "title", e.target.value)
                    }
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea
                    value={item.description || ""}
                    onChange={(e) =>
                      handleFieldChange(item.id, "description", e.target.value)
                    }
                    style={{ ...inputStyle, minHeight: 70, resize: "vertical" }}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr auto",
                    gap: 12,
                    alignItems: "end",
                  }}
                >
                  <div>
                    <label style={labelStyle}>Weight</label>
                    <input
                      type="number"
                      step="0.1"
                      value={item.weight}
                      onChange={(e) =>
                        handleFieldChange(item.id, "weight", e.target.value)
                      }
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Order</label>
                    <input
                      type="number"
                      value={item.order}
                      onChange={(e) =>
                        handleFieldChange(item.id, "order", e.target.value)
                      }
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Scoring Type</label>
                    <select
                      value={item.scoring_type}
                      onChange={(e) =>
                        handleFieldChange(item.id, "scoring_type", e.target.value)
                      }
                      style={inputStyle}
                    >
                      <option value="distance_inverse">distance_inverse</option>
                      <option value="threshold_band">threshold_band</option>
                      <option value="boolean">boolean</option>
                      <option value="custom">custom</option>
                    </select>
                  </div>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      paddingBottom: 10,
                      fontWeight: 600,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={item.is_active}
                      onChange={(e) =>
                        handleFieldChange(item.id, "is_active", e.target.checked)
                      }
                    />
                    Active
                  </label>
                </div>

                <div>
                  <label style={labelStyle}>Config</label>

                  {item.scoring_type === "distance_inverse" && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 12,
                      }}
                    >
                      <div>
                        <label style={labelStyle}>Max Distance (km)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={item.config?.max_distance_km ?? ""}
                          onChange={(e) =>
                            handleConfigFieldChange(
                              item.id,
                              "max_distance_km",
                              Number(e.target.value)
                            )
                          }
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>Min Score</label>
                        <input
                          type="number"
                          step="0.1"
                          value={item.config?.min_score ?? ""}
                          onChange={(e) =>
                            handleConfigFieldChange(
                              item.id,
                              "min_score",
                              Number(e.target.value)
                            )
                          }
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>Max Score</label>
                        <input
                          type="number"
                          step="0.1"
                          value={item.config?.max_score ?? ""}
                          onChange={(e) =>
                            handleConfigFieldChange(
                              item.id,
                              "max_score",
                              Number(e.target.value)
                            )
                          }
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  )}

                  {item.scoring_type === "boolean" && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                      }}
                    >
                      <div>
                        <label style={labelStyle}>True Score</label>
                        <input
                          type="number"
                          value={item.config?.true_score ?? ""}
                          onChange={(e) =>
                            handleConfigFieldChange(
                              item.id,
                              "true_score",
                              Number(e.target.value)
                            )
                          }
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>False Score</label>
                        <input
                          type="number"
                          value={item.config?.false_score ?? ""}
                          onChange={(e) =>
                            handleConfigFieldChange(
                              item.id,
                              "false_score",
                              Number(e.target.value)
                            )
                          }
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  )}

                  {item.scoring_type === "threshold_band" && (
                    <div style={{ display: "grid", gap: 10 }}>
                      {(item.config?.bands || []).map((band, index) => (
                        <div
                          key={index}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr auto",
                            gap: 12,
                            alignItems: "end",
                            padding: 12,
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                          }}
                        >
                          <div>
                            <label style={labelStyle}>Max Distance</label>
                            <input
                              type="number"
                              step="0.1"
                              value={band.max}
                              onChange={(e) =>
                                handleBandChange(
                                  item.id,
                                  index,
                                  "max",
                                  e.target.value
                                )
                              }
                              style={inputStyle}
                            />
                          </div>

                          <div>
                            <label style={labelStyle}>Score</label>
                            <input
                              type="number"
                              step="0.1"
                              value={band.score}
                              onChange={(e) =>
                                handleBandChange(
                                  item.id,
                                  index,
                                  "score",
                                  e.target.value
                                )
                              }
                              style={inputStyle}
                            />
                          </div>

                          <button
                            onClick={() => removeBand(item.id, index)}
                            style={{
                              ...buttonBaseStyle,
                              border: "1px solid #f04438",
                              background: "#fff",
                              color: "#d92d20",
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}

                      <div>
                        <button
                          onClick={() => addBand(item.id)}
                          style={{
                            ...buttonBaseStyle,
                            border: "1px solid #d0d5dd",
                            background: "#fff",
                            color: "#101828",
                          }}
                        >
                          Add Band
                        </button>
                      </div>
                    </div>
                  )}

                  {item.scoring_type === "custom" && (
                    <div
                      style={{
                        padding: 12,
                        border: "1px dashed #d0d5dd",
                        borderRadius: 10,
                        color: "#667085",
                      }}
                    >
                      Custom scoring does not have a dedicated form yet. A separate editor
                      for this type will be built later.
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    justifyContent: "flex-end",
                    marginTop: 4,
                  }}
                >
                  <button
                    onClick={() => handleReset(item.id)}
                    disabled={!changed || savingId === item.id}
                    style={{
                      ...buttonBaseStyle,
                      border: "1px solid #d0d5dd",
                      background: "#fff",
                      color: "#101828",
                      cursor: changed ? "pointer" : "not-allowed",
                    }}
                  >
                    Reset
                  </button>

                  <button
                    onClick={() => handleSave(item)}
                    disabled={!changed || savingId === item.id}
                    style={{
                      ...buttonBaseStyle,
                      border: "none",
                      background: changed ? "#101828" : "#d0d5dd",
                      color: "#fff",
                      cursor: changed ? "pointer" : "not-allowed",
                    }}
                  >
                    {savingId === item.id ? "Saving..." : "Save Criterion"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoreCriteriaDashboard;